import { cn } from '@nesso/ui/lib/utils';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@nesso/ui/primitives/dialog';
import { Separator } from '@nesso/ui/primitives/separator';

import { BillingHistory } from '@/entities/billing-history';
import {
    formatPaymentMethod,
    formatPaymentNumber,
    formatRefundNumber,
    type Payment,
} from '@/entities/payment';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';
import { formatNumericDate } from '@/shared/lib/time-utils';

type Props = {
    payment: Payment;
    highlightedSessionChargeId: string | null;
    getServiceLabel: (serviceCode: string) => string;
    onClose: () => void;
};

export function ClientBillingSheetAppliedToDialog({
    payment,
    highlightedSessionChargeId,
    getServiceLabel,
    onClose,
}: Props) {
    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-xl leading-7">
                        {formatPaymentNumber(payment.number)}
                    </DialogTitle>
                    <DialogDescription>{formatNumericDate(payment.paidAt)}</DialogDescription>
                </DialogHeader>
                <div className="pb-2">
                    <div className="flex flex-col gap-4 text-sm">
                        <section className="flex flex-col gap-2">
                            <h4 className="font-bold">Applied to</h4>
                            <ul className="flex flex-col gap-0.5">
                                {payment.appliedTo.map((appliedTo) => (
                                    <li
                                        key={appliedTo.sessionChargeId}
                                        aria-current={
                                            payment.appliedTo.length > 1 &&
                                            appliedTo.sessionChargeId === highlightedSessionChargeId
                                                ? 'true'
                                                : undefined
                                        }
                                        className={cn(
                                            'w-fit rounded-xs aria-[current=true]:bg-accent aria-[current=true]:px-0.5',
                                            payment.refund && 'line-through',
                                        )}
                                    >
                                        {formatNumericDate(appliedTo.date)},{' '}
                                        {getServiceLabel(appliedTo.serviceCode)}
                                    </li>
                                ))}
                                {payment.refund && (
                                    <li className="flex items-center justify-between">
                                        <span>{formatRefundNumber(payment.refund.number)}</span>
                                        <span>
                                            {formatCurrencyFromCents(-payment.refund.amountCents)}
                                        </span>
                                    </li>
                                )}
                            </ul>
                        </section>
                        <section className="flex flex-col gap-1">
                            <h4 className="font-bold">Method</h4>
                            <p>{formatPaymentMethod(payment.method, payment.checkNumber)}</p>
                        </section>
                        <Separator />
                        <section className="flex flex-col gap-2">
                            <h4 className="font-bold">History</h4>
                            <BillingHistory entries={payment.history} />
                        </section>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
